"use client"

import { useEffect } from "react"

/**
 * Reposiciona o trigger ("Reportar um bug") do Sentry Feedback para
 * ficar ACIMA do mobile dock em telas <lg. Sem isso, o botão fica
 * em bottom-right (default) atrás do dock — e em mobile o toque
 * cai no dock, não no botão.
 *
 * O Sentry Feedback v10 expõe estas CSS vars no host do Shadow DOM:
 *   --actor-inset    (default: var(--inset))
 *   --inset          (default: "auto 0 0 auto" = bottom-right)
 *   --z-index        (default: 100000)
 *
 * Sobrescrevendo --actor-inset no elemento host com id="sentry-feedback",
 * o valor é herdado dentro do Shadow DOM pelo `.widget__actor`.
 *
 * Em desktop deixa o default. Em mobile, sobe pra ficar visível
 * E clicável acima do dock (que mora em bottom-0 com z-[55]).
 */

const MOBILE_BREAKPOINT = 1024
// Dock = h-14 (56px) + safe-area; somamos +12px de respiro acima dele
const ACTOR_INSET_MOBILE = "auto 12px calc(env(safe-area-inset-bottom, 0px) + 5.25rem) auto"

function applyInset(host: HTMLElement, isMobile: boolean) {
  if (isMobile) {
    host.style.setProperty("--actor-inset", ACTOR_INSET_MOBILE)
    host.style.setProperty("--page-margin", "0px")
    host.style.zIndex = "100000"
  } else {
    host.style.removeProperty("--actor-inset")
    host.style.removeProperty("--page-margin")
    host.style.removeProperty("z-index")
  }
}

export default function SentryFeedbackPositioner() {
  useEffect(() => {
    if (typeof window === "undefined") return

    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)

    const findHost = (): HTMLElement | null =>
      document.getElementById("sentry-feedback") as HTMLElement | null

    let host = findHost()
    if (host) applyInset(host, mql.matches)

    const observer = new MutationObserver(() => {
      const candidate = findHost()
      if (candidate && candidate !== host) {
        host = candidate
        applyInset(host, mql.matches)
      }
    })
    observer.observe(document.body, { childList: true, subtree: false })

    const onChange = () => {
      const current = findHost()
      if (current) applyInset(current, mql.matches)
    }
    mql.addEventListener("change", onChange)
    window.addEventListener("resize", onChange)

    return () => {
      observer.disconnect()
      mql.removeEventListener("change", onChange)
      window.removeEventListener("resize", onChange)
    }
  }, [])

  return null
}
